import React, { useState, useEffect, useMemo } from 'react';
import {
  C, storage, THEME, setTheme, ingestTemaSuite,
  DEFAULT_PANELS, DEFAULT_INVERTERS, DEFAULT_BATTERIES, DEFAULT_PRICING, OPERATORS
} from './constants';
import Home from './components/Home';
import Servicios from './components/Servicios';
import Quoter from './components/Quoter';
import EstudioFlux from './components/EstudioFlux';
import Estudio from './components/Estudio';
import InstallerReg from './components/InstallerReg';
import BackOffice from './components/BackOffice';
import SupplierPortal from './components/SupplierPortal';
import TechnicianPortal from './components/TechnicianPortal';
import QuoteTracking from './components/QuoteTracking';
import MisCotizaciones from './components/MisCotizaciones';
import FluxaiEmbed from './components/FluxaiEmbed';
import Ordenes from './components/Ordenes';
import Tienda from './components/Tienda';
import Login from './components/Login';
import Privacidad from './components/Privacidad';
import Terminos from './components/Terminos';
import AlexLauncher from './components/AlexLauncher';
import { fetchLoadsCatalog, DEFAULT_LOADS_CATALOG } from './services/loads';
import { currentClaims, audience, isSignedToken, getToken, profileLabel, logout as authLogout, ingestSsoToken, isTecnico } from './services/auth';
import { syncEntitlements, syncAllEntitlements } from './services/entitlements';
import { listMyQuotesRemote, listQuotesRemote, mapRemoteQuote, updateQuoteRemote } from './services/quotes';
import { veEstudioFotovoltaico, veMonitoreo, veCatalogoServicios, asegurarAltaTecnico } from './services/acceso';
import Monitoreo from './components/Monitoreo';
import { syncUsuarios } from './services/usuarios';
import { pushOrden, fetchMisOrdenes, fetchAllOrdenes } from './services/ordenes';
import ServiceIcon from './components/ServiceIcon';
import { fluxaiAppUrl } from './services/fluxai';
import { leerOfertasProveedores, mezclarFichasModelo } from './services/catalogo-equipo';
import { aplicarCatalogoFichas } from './services/productos';
import { cargarFichasHub } from './services/fichas';

// Gateo de navegación por rol (identidad compartida con FluxAI). Mientras NO haya
// sesión FIRMADA (login real de api/), es default-allow: no se oculta nada para no
// romper el flujo actual. Con sesión firmada, cada audiencia ve solo lo suyo.
const NAV_RULES = {
  home: true,                                   // todos
  servicios: ['admin', 'operator'],             // catálogo: solo gestión. El usuario usa Solar Hub.
  quoter: ['operator', 'user', 'technician', 'supplier'], // el admin no lo ve (menú aprobado 26-oct); premium se gatea aparte
  estudio: ['operator', 'user', 'technician'], // premium SH, no suite gratis; el admin no lo ve (26-oct)
  monitoreo: ['admin', 'operator', 'user', 'technician'],
  instalador: ['technician', 'user'],           // user: «Quiero ser técnico» (15-sep); el admin da de alta desde Administración (26-oct)
  proveedor: ['supplier', 'operator', 'user'],  // con sesión firmada no se pierde «Vender equipos»; el admin no (26-oct)
  backoffice: ['admin'],
};
const NAV_DEF = [
  { id: 'home',            section: null },                                              // todos
  { id: 'estudio',         section: 'Solar Hub' },                                       // producto principal
  { id: 'quoter',          section: 'Solar Hub' },                                       // cotización gratis, secundaria
  { id: 'monitoreo',       section: 'Solar Hub',            aud: ['admin', 'operator', 'user', 'technician'] },
  { id: 'misCotizaciones', section: 'Solar Hub',            aud: ['user', 'technician'] },
  { id: 'servicios',       section: 'Sistema',              aud: ['admin', 'operator'] }, // catálogo: no es menú del usuario
  { id: 'tienda',          section: 'Solar Hub' },                                       // Tienda de equipos: todos
  // Órdenes de servicio: instalación (técnico) → soporte. Gestor ve todas; técnico las suyas;
  // el CLIENTE ve el estado de SU instalación y solicita/realiza seguimiento del soporte posventa.
  { id: 'ordenes',         section: 'Operación y soporte',  aud: ['admin', 'operator', 'technician', 'user'] },
  { id: 'soporte',         section: 'Operación y soporte',  aud: ['admin', 'operator', 'technician'] },
  { id: 'instalador',      section: 'Operación y soporte',  aud: ['technician', 'user'] }, // alta RETIE: INTERNA (dueño, 6-sep); admin fuera (26-oct)
  { id: 'proveedor',       section: 'Operación y soporte' },                             // alta de proveedor
  { id: 'backoffice',      section: 'Sistema',              aud: ['admin', 'operator'] }, // gestión
];

// Cotizador + estudio + tienda: frente comercial. El invitado (aud=null) también los ve.
// 🔴 `instalador` YA NO: el Invitado veía el formulario RETIE entero en «Instalar» (captura del
// dueño, 6-sep: «debe haber validación e inscripción previa para abrir el portal»). `proveedor` se
// queda porque NO es un alta: es el login del Portal, y sin fila en `suppliers` no entra nadie.
// Guarda: `src/__tests__/alta-retie-interna.test.js`.
const HERRAMIENTAS_PUBLICAS = ['estudio', 'quoter', 'tienda', 'proveedor'];
function navAllowed(id, aud, signed) {
  if (!signed) return true;                     // sin login real → no gatear
  const rule = NAV_RULES[id];
  if (rule === true || rule == null) return true;
  return rule.includes(aud);
}

// Iconos de navegación monocromáticos (estilo software, sin emojis) — heredan
// el color del botón vía currentColor (FluxAI usa el mismo patrón de rail).
function NavIcon({ id, size = 18 }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (id) {
    case 'home':       return <svg {...p}><path d="M3 11 12 4l9 7"/><path d="M6 10v9h4v-5h4v5h4v-9"/></svg>;
    case 'servicios':  return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1.2"/><rect x="14" y="3" width="7" height="7" rx="1.2"/><rect x="3" y="14" width="7" height="7" rx="1.2"/><rect x="14" y="14" width="7" height="7" rx="1.2"/></svg>;
    case 'misCotizaciones': return <svg {...p}><path d="M8 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M8 11h8M8 15h6"/></svg>;
    case 'ordenes':    return <svg {...p}><path d="M9 11l3 3 8-8"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>;
    case 'soporte':    return <svg {...p}><circle cx="12" cy="12" r="9"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>;
    case 'quoter':     return <svg {...p}><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8"/><path d="M8 10h2M12 10h.01M16 10h.01M8 14h2M12 14h.01M16 14h.01M8 18h4"/></svg>;
    case 'instalador': return <svg {...p}><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.1-.4-.4-2.1z"/></svg>;
    case 'proveedor':  return <svg {...p}><path d="M21 8 12 3 3 8v8l9 5 9-5z"/><path d="M3 8l9 5 9-5M12 13v8"/></svg>;
    case 'backoffice': return <svg {...p}><path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1"/><circle cx="15" cy="6" r="2"/><circle cx="9" cy="12" r="2"/><circle cx="17" cy="18" r="2"/></svg>;
    case 'tienda':     return <svg {...p}><path d="M3 9l1.5-5h15L21 9"/><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9"/><path d="M3 9h18"/><path d="M9 13h6"/></svg>;
    case 'estudio':    return <svg {...p}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"/></svg>;
    case 'monitoreo':  return <svg {...p}><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M7 16v3M17 16v3M8 20h8M7 10h2M11 8h2M15 11h2"/></svg>;
    default: return null;
  }
}

// Isotipos REALES de la suite (identidad corporativa).
// Solar Hub = sol de Alebas (rayos + nodos + centro) — idéntico al del sidebar.
function SunMark({ size = 16 }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} style={{ flexShrink: 0 }} aria-hidden="true">
      <defs><radialGradient id="suiteSun2" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#F9B000"/><stop offset="100%" stopColor="#EB5D0B"/></radialGradient></defs>
      {[0, 60, 120, 180, 240, 300].map(d => { const r = (d - 90) * Math.PI / 180; return <line key={'l' + d} x1={20 + 10 * Math.cos(r)} y1={20 + 10 * Math.sin(r)} x2={20 + 18 * Math.cos(r)} y2={20 + 18 * Math.sin(r)} stroke={d % 120 === 0 ? '#EB5D0B' : '#F9B000'} strokeWidth="1.8" strokeLinecap="round"/>; })}
      {[0, 60, 120, 180, 240, 300].map(d => { const r = (d - 90) * Math.PI / 180; return <circle key={'c' + d} cx={20 + 18.5 * Math.cos(r)} cy={20 + 18.5 * Math.sin(r)} r="2.2" fill={d % 120 === 0 ? '#EB5D0B' : '#F9B000'}/>; })}
      <circle cx="20" cy="20" r="9" fill="url(#suiteSun2)"/><circle cx="20" cy="20" r="4.5" fill="#002430"/><circle cx="20" cy="20" r="2.2" fill="#F9B000"/>
    </svg>
  );
}
// FluxAI = isotipo OFICIAL (rayo con degradé verde→azul→verde, sin fondo) — del `BrandLogo` de FluxAI.
function FluxBoltMark({ size = 16 }) {
  return (
    <svg viewBox="0 0 91 91" width={size} height={size} fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
      <path d="M64 14L32.7647 40.7966H52.8824L28 76L57.1176 47.6271H37.5294L64 14Z" stroke="url(#fluxMarkGrad)" strokeWidth="4" strokeLinejoin="round" />
      <defs>
        <linearGradient id="fluxMarkGrad" x1="46" y1="14" x2="46" y2="76" gradientUnits="userSpaceOnUse">
          <stop stopColor="#00C781" /><stop offset="0.27" stopColor="#0044A4" /><stop offset="0.49" stopColor="#0C62DC" /><stop offset="0.69" stopColor="#0044A4" /><stop offset="1" stopColor="#00C781" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// Reloj en vivo (fecha + hora Colombia) para la barra superior — tic cada 30 s. Igual que la
// consola FluxAI (densidad de "software", no de página web).
function HeaderClock() {
  const [now, setNow] = useState(null);
  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!now) return null;
  const fecha = now.toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short' });
  const hora = now.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  return (
    <span title="Fecha y hora (Colombia)" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, color: C.muted, whiteSpace: 'nowrap' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
      <span style={{ textTransform: 'capitalize' }}>{fecha}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', color: C.heading }}>{hora}</span>
    </span>
  );
}

// Estado del trabajo en sesión (equivalente al indicador de flota de FluxAI): para gestores,
// nº de cotizaciones + órdenes abiertas; para el cliente, sus órdenes en curso. Dato REAL del
// estado, no inventado. Compacto, estilo barra de consola.
function HeaderStatus({ quotes, ordenes, aud }) {
  const abiertas = (ordenes || []).filter(o => {
    const e = String(o?.estado || '').toLowerCase();
    return e && !['cerrada', 'finalizada', 'resuelto', 'completada', 'cancelada'].includes(e);
  }).length;
  const pill = {
    display: 'flex', alignItems: 'center', gap: 5, borderRadius: 5,
    border: `1px solid ${C.border}`, padding: '2px 7px', fontSize: 11, fontWeight: 600,
    color: C.muted, textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'default',
  };
  if (aud === 'admin' || aud === 'operator') {
    return (
      <>
        <span style={pill} title="Cotizaciones en el sistema">
          <span style={{ width: 6, height: 6, borderRadius: 999, background: C.green, display: 'inline-block' }} />
          <span style={{ fontVariantNumeric: 'tabular-nums', color: C.heading }}>{(quotes || []).length}</span>
          <span style={{ color: C.muted }}>cotiz.</span>
        </span>
        {abiertas > 0 && (
          <span style={{ ...pill, border: `1px solid ${C.yellow}55`, background: `${C.yellow}14`, color: C.yellow }} title="Órdenes de servicio abiertas">
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{abiertas}</span>
            <span>órden{abiertas === 1 ? '' : 'es'}</span>
          </span>
        )}
      </>
    );
  }
  if (aud === 'user' && abiertas > 0) {
    return (
      <span style={{ ...pill, border: `1px solid ${C.teal}55`, background: `${C.teal}14`, color: C.teal }} title="Tus órdenes en curso">
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{abiertas}</span>
        <span>en curso</span>
      </span>
    );
  }
  return null;
}

// Lanzador de la SUITE Alebas (estilo Office): cambia a FluxAI en SU propia ventana/PWA (target con
// nombre fijo → se reusa) + SSO adjunto (sin re-login). Cada app con su logo y su color.
function SuiteLauncher() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="sh-btn sh-btn-icon" onClick={() => setOpen(v => !v)} title="Suite Alebas" aria-label="Apps de Alebas">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 95 }} />
          <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 96, width: 244, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, boxShadow: '0 10px 28px rgba(0,0,0,.2)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, padding: '2px 6px 6px' }}>Suite Alebas</div>
            <div className="sh-suite-item" style={{ background: `${C.teal}18` }}>
              <SunMark/> <span style={{ fontWeight: 600, fontSize: 13, color: C.heading }}>Solar Hub</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: C.muted }}>Cotizar / diseñar PV</span>
            </div>
            <a href={fluxaiAppUrl()} target="alebas-fluxai" rel="noopener" onClick={() => setOpen(false)} className="sh-suite-item" style={{ marginTop: 4 }}>
              <FluxBoltMark/> <span style={{ fontWeight: 600, fontSize: 13, color: C.heading }}>FluxAI</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: C.muted }}>Telemetría / monitoreo</span>
            </a>
          </div>
        </>
      )}
    </div>
  );
}

function AppShell() {
  const [hydrated, setHydrated] = useState(false);
  // Por que se rebotó al Inicio. `null` = no se rebotó a nadie.
  const [avisoRuta, setAvisoRuta] = useState(null);
  const [view, setView] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('view');
      // 🔴 Esta lista estaba ESCRITA A MANO con seis vistas, así que un deep link a cualquier otra
      // —`monitoreo`, `misCotizaciones`, `ordenes`, `backoffice`…— aterrizaba en Inicio con la URL
      // diciendo otra cosa. Y como la URL YA traía el parámetro, volver a pulsar la tarjeta no
      // cambiaba nada: la vista quedaba muerta hasta navegar a otra y volver.
      //
      // Peor: el guard de abajo tiene una excepción `deepLinkAdmin` para `?view=backoffice` (PR
      // #299) que era INALCANZABLE desde una URL, porque este inicializador nunca producía
      // `backoffice`. El arreglo se le aplicó al guard y no a la puerta de entrada.
      //
      // Se honra cualquier vista declarada en el menú; quién PUEDE verla ya lo decide el guard de
      // ruta, que bota al inicio lo que no corresponda al rol. Una sola autoridad, no dos listas.
      if (v && (v === 'login' || NAV_DEF.some((i) => i.id === v))) return v;
    } catch { /* SSR / privado */ }
    return 'home';
  });
  // Handle del estudio de FluxAI: `?view=estudio&fx=fx:…`. Sin él, la vista `estudio` sigue
  // montando el motor NATIVO (`alto`) — la paralización del estudio de Solar Hub es el paso 5
  // del plan de FluxAI y está gateada a que el 4 se verifique EN PRODUCCIÓN. Leer el estudio
  // ajeno no habilita apagar el propio.
  const [fxHandle] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('fx') || ''; }
    catch { return ''; }
  });
  const [boTab, setBoTab] = useState('dashboard');
  const [fluxSku, setFluxSku] = useState(null); // servicio FluxAI embebido en curso
  const openFlux = (sku) => { setFluxSku(sku); setView('fluxaiEmbed'); };
  const [panels, setPanels] = useState(DEFAULT_PANELS);
  const [inverters, setInverters] = useState(DEFAULT_INVERTERS);
  const [batteries, setBatteries] = useState(DEFAULT_BATTERIES);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [operators, setOperators] = useState(OPERATORS);
  const [quotes, setQuotes] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  // 🔴 «NO TIENES» Y «NO SE PUDO PREGUNTAR» NO SON LO MISMO, y hasta acá se veían igual.
  // Las hidrataciones de abajo hacen `if (!r?.ok) return;` — correcto para NO borrar lo local,
  // pero la señal se perdía ahí. Con el local vacío (equipo nuevo, storage limpio) la pantalla
  // pasaba a afirmar «Aún no tienes cotizaciones» / «Aún no tienes órdenes de servicio»: un
  // hecho sobre el historial comercial del titular apoyado en una consulta que falló.
  // 'pendiente' = todavía no se intentó · 'ok' = el servidor contestó · 'fallo' = no se pudo.
  const [sincro, setSincro] = useState({ quotes: 'pendiente', ordenes: 'pendiente', usuarios: 'pendiente' });
  const [suppliers, setSuppliers] = useState([]);
  const [loadsCatalog, setLoadsCatalog] = useState(DEFAULT_LOADS_CATALOG);
  const [loadsSource, setLoadsSource] = useState('local-default');

  useEffect(() => {
    // Union-merge equipment lists: keep saved items (custom prices/specs) AND
    // inject any new default items added since the user's last visit.
    const gMerge = (k, defaults, setter) => {
      try {
        const r = storage.get(k);
        if (r?.value) {
          const saved = JSON.parse(r.value);
          const ids = new Set(saved.map(x => x.id));
          const merged = [...saved, ...defaults.filter(d => !ids.has(d.id))];
          setter(merged);
          return;
        }
      } catch {}
    };
    const g = (k, s) => {
      try { const r = storage.get(k); if (r?.value) s(JSON.parse(r.value)); } catch {}
    };
    gMerge('al:panels', DEFAULT_PANELS, setPanels);
    gMerge('al:inverters', DEFAULT_INVERTERS, setInverters);
    gMerge('al:batteries', DEFAULT_BATTERIES, setBatteries);
    g('al:pricing', setPricing);
    g('al:operators', setOperators);
    g('al:quotes', setQuotes);
    g('al:installers', setInstallers);
    g('al:ordenes', setOrdenes);
    g('al:suppliers', setSuppliers);
    // Intento de hidratar cuadro de cargas desde el hub-api (no bloquea UI).
    fetchLoadsCatalog().then(d => {
      if (Array.isArray(d?.items) && d.items.length) {
        setLoadsCatalog(d.items);
        setLoadsSource(d.source || 'hub-api');
      }
    }).catch(() => {});
    setHydrated(true);
  }, []);

  // Hidrata desde el BACKEND (Postgres, por el hub-api) lo que el usuario tiene CONTRATADO y la
  // administración de usuarios → cross-device (lo que el admin asigna en un equipo lo ve el
  // usuario en cualquier otro). Corre al montar y en cada cambio de sesión (`solarhub-auth`).
  useEffect(() => {
    // Dedupe de cotizaciones: el remoto (server, autoritativo) gana sobre el local del dispositivo.
    const keyDe = (q) => q.dedupeKey || q.verifiedToken ||
      (q.email && q.date ? `${(q.email || '').toLowerCase()}|${q.date}|${q.results?.actKwp ?? ''}` : null) || String(q.id);
    const mezclarQuotes = (incoming, prev) => {
      const seen = new Map();
      for (const q of [...incoming, ...prev]) {           // incoming (server) primero → gana
        const k = keyDe(q);
        if (!seen.has(k)) seen.set(k, q);
      }
      return [...seen.values()];
    };
    const hidratarSuite = () => {
      // Un fallo de red no puede quedar mudo: la pantalla lo necesita para no afirmar
      // ausencia. No borra nada de lo local — solo dice si el servidor llegó a contestar.
      const marcar = (sup, estado) => setSincro((s) => (s[sup] === estado ? s : { ...s, [sup]: estado }));
      const c = currentClaims();
      const email = (c?.email || '').toLowerCase();
      const aud = audience(c);
      if (email) syncEntitlements(email);            // gateo del propio usuario
      if (aud === 'admin') {                          // el admin ve todas las asignaciones + usuarios
        syncAllEntitlements();
        syncUsuarios().then((ok) => marcar('usuarios', ok ? 'ok' : 'fallo')).catch(() => marcar('usuarios', 'fallo'));
      }
      // Cotizaciones CROSS-DEVICE: el cliente ve LAS SUYAS (por correo) desde cualquier equipo;
      // admin/operador ven todas. Reemplaza el desorden del localStorage por dato del servidor.
      if (aud === 'user' && email) {
        listMyQuotesRemote({ email }).then(r => {
          if (!r?.ok || !Array.isArray(r.quotes)) { marcar('quotes', 'fallo'); return; }
          // Sin sesión verificada el servidor responde 401 (#435): se avisa «sin_sesion», no «fallo».
          if (r.sesion_firmada === false) marcar('quotes', 'sin_sesion'); else marcar('quotes', 'ok');
          const mine = r.quotes.map(mapRemoteQuote).filter(Boolean);
          setQuotes(prev => {
            // del local, conserva SOLO las del propio correo (descarta cotizaciones ajenas del equipo)
            const localMine = prev.filter(q => (q.email || '').toLowerCase() === email);
            const merged = mezclarQuotes(mine, localMine);
            sv('al:quotes', merged); return merged;
          });
        }).catch((e) => marcar('quotes', e?.status === 401 ? 'sin_sesion' : 'fallo'));
      } else if (aud === 'admin' || aud === 'operator') {
        listQuotesRemote({ limit: 500 }).then(r => {
          if (!r?.ok || !Array.isArray(r.quotes)) { marcar('quotes', 'fallo'); return; }
          marcar('quotes', 'ok');
          const all = r.quotes.map(mapRemoteQuote).filter(Boolean);
          setQuotes(prev => { const merged = mezclarQuotes(all, prev); sv('al:quotes', merged); return merged; });
        }).catch(() => marcar('quotes', 'fallo'));
      }
      // Órdenes de servicio CROSS-DEVICE (Postgres, por el hub-api): el cliente CONSULTA el estado
      // de SU(S) orden(es) por correo desde cualquier equipo; admin/operador ven todas. El
      // servidor es autoritativo (gana por id); se preservan las locales aún no sincronizadas.
      const mergeOrdenesById = (incoming, prev) => {
        const seen = new Map();
        for (const o of [...incoming, ...prev]) { if (o && o.id && !seen.has(o.id)) seen.set(o.id, o); }
        return [...seen.values()];
      };
      if (aud === 'user' && email) {
        fetchMisOrdenes(email).then(list => {
          if (!Array.isArray(list)) { marcar('ordenes', 'fallo'); return; }
          marcar('ordenes', 'ok');
          setOrdenes(prev => {
            const localMine = prev.filter(o => (o.email || '').toLowerCase() === email); // descarta órdenes ajenas del equipo
            const merged = mergeOrdenesById(list, localMine);
            sv('al:ordenes', merged); return merged;
          });
        }).catch(() => marcar('ordenes', 'fallo'));
      } else if (aud === 'admin' || aud === 'operator') {
        fetchAllOrdenes().then(list => {
          if (!Array.isArray(list)) { marcar('ordenes', 'fallo'); return; }
          marcar('ordenes', 'ok');
          setOrdenes(prev => { const merged = mergeOrdenesById(list, prev); sv('al:ordenes', merged); return merged; });
        }).catch(() => marcar('ordenes', 'fallo'));
      }
    };
    hidratarSuite();
    window.addEventListener('solarhub-auth', hidratarSuite);
    return () => window.removeEventListener('solarhub-auth', hidratarSuite);
  }, []);

  const sv = (k, d) => storage.set(k, JSON.stringify(d));
  const uP = d => { setPanels(d); sv('al:panels', d); };
  const uI = d => { setInverters(d); sv('al:inverters', d); };
  const uB = d => { setBatteries(d); sv('al:batteries', d); };
  const uPr = d => { setPricing(d); sv('al:pricing', d); };
  const uOp = d => { setOperators(d); sv('al:operators', d); };
  const addQ = q => { const n = [q, ...quotes]; setQuotes(n); sv('al:quotes', n); };
  const updQ = q => {
    const n = quotes.map(x => x.id === q.id ? q : x); setQuotes(n); sv('al:quotes', n);
    // Si la cotización vive en el backend (id 'r_<id>' o _remote), persiste el cambio (estado/nota)
    // → cross-device (admin cambia estado / cliente acepta y se ve en cualquier equipo).
    if (q._remote || String(q.id).startsWith('r_')) {
      updateQuoteRemote({ id: q.id, status: q.status, notes: q.cliente_nota }).catch(() => {});
    }
  };
  // Cliente acepta la propuesta → cotización 'ganada' Y se genera la ORDEN DE SERVICIO
  // SIN técnico (estado 'programada'); el admin asigna el técnico después (lógica de plataforma).
  const aceptarCotizacion = (q) => {
    updQ({ ...q, status: 'ganada', cliente_acepto_en: new Date().toISOString() });
    if (!ordenes.some(o => o.quoteId === q.id)) {
      addOrden({
        id: 'os_' + Math.random().toString(36).slice(2, 10), quoteId: q.id, tipo: 'instalacion',
        cliente: q.name || q.company || '—', email: q.email || '',
        sistema: q.systemType || '—', kwp: q.results?.actKwp ?? null, inversion: q.budget?.tot ?? null,
        valor: q.budget?.tot ?? null,
        direccion: q.address || '', tecnicoId: null, tecnicoNombre: '', tecnicoEmail: '',
        estado: 'programada', fecha: new Date().toISOString(),
        notas: 'Generada al aceptar la propuesta — falta asignar técnico.', soporte: [],
      });
    }
  };
  const addInst = i => { const n = [i, ...installers]; setInstallers(n); sv('al:installers', n); };
  // Las órdenes viven en el BACKEND (Postgres, por el hub-api) → cross-device. Cada mutación
  // (crear / avanzar estado / asignar técnico / ticket) persiste la orden COMPLETA;
  // la caché local es optimista. (Si no hay backend, queda local — degradación elegante.)
  const addOrden = o => { const n = [o, ...ordenes]; setOrdenes(n); sv('al:ordenes', n); pushOrden(o); };
  const updOrden = o => { const n = ordenes.map(x => x.id === o.id ? o : x); setOrdenes(n); sv('al:ordenes', n); pushOrden(o); };
  const addSupp = s => { const n = [s, ...suppliers]; setSuppliers(n); sv('al:suppliers', n); };
  const uSupp = d => { setSuppliers(d); sv('al:suppliers', d); };

  const logout = () => { try { localStorage.removeItem('sh:admin'); } catch {} authLogout(); setView('home'); };

  // Identidad compartida (FluxAI): audiencia de la sesión + si el token está firmado.
  const claims = currentClaims();
  const aud = audience(claims);
  const signed = isSignedToken(getToken());
  if (isTecnico(claims)) asegurarAltaTecnico(claims);
  // El cotizador modela con SUITE + oferta de proveedores. El catálogo editable
  // de Administración (al:panels/…) se queda en lo nuestro para no ensuciarlo.
  const [ofertaTick, setOfertaTick] = useState(0);
  useEffect(() => {
    const bump = () => setOfertaTick((n) => n + 1);
    window.addEventListener('sh-fichas-oferta', bump);
    return () => window.removeEventListener('sh-fichas-oferta', bump);
  }, []);
  const ofertasEq = useMemo(() => leerOfertasProveedores(), [ofertaTick]);
  const fichasPaneles = useMemo(() => mezclarFichasModelo(panels, ofertasEq, 'panel'), [panels, ofertasEq]);
  const fichasInversores = useMemo(() => mezclarFichasModelo(inverters, ofertasEq, 'inverter'), [inverters, ofertasEq]);
  const fichasBaterias = useMemo(() => mezclarFichasModelo(batteries, ofertasEq, 'battery'), [batteries, ofertasEq]);
  useEffect(() => {
    aplicarCatalogoFichas({ panels: fichasPaneles, inverters: fichasInversores, batteries: fichasBaterias });
  }, [fichasPaneles, fichasInversores, fichasBaterias]);
  useEffect(() => {
    cargarFichasHub().then((f) => {
      const merge = (cur, extra) => {
        if (!extra.length) return cur;
        const seen = new Set(cur.map((x) => `${String(x.brand || '').toLowerCase()}|${String(x.model || '').toLowerCase()}`));
        const add = extra.filter((x) => {
          const k = `${String(x.brand || '').toLowerCase()}|${String(x.model || '').toLowerCase()}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        }).map((x) => ({ ...x, origen: x.origen || 'alebas' }));
        return add.length ? [...cur, ...add] : cur;
      };
      if (f.panels.length) setPanels((p) => merge(p, f.panels));
      if (f.inverters.length) setInverters((p) => merge(p, f.inverters));
      if (f.batteries.length) setBatteries((p) => merge(p, f.batteries));
    }).catch(() => {});
  }, []);

  // Menú DIFERENCIAL por rol (modelo FluxAI: cada ítem declara las AUDIENCIAS que lo ven).
  // Las funciones/roles son, al final, SERVICIOS que un cliente contrata → el menú gatea por
  // (audiencia × entitlement). El ADMIN administra todo desde Administración; NO entra a los
  // portales de proveedor/instalador (esos son la consola de ESE rol, no del admin).
  //   admin/operator → gestionan; user (cliente) → cotiza y consulta; technician → instala;
  //   supplier → su portal de marketplace.
  const NAV_LABELS = {
    home: 'Inicio', servicios: 'Servicios', quoter: 'Cotizador', estudio: 'Estudio fotovoltaico',
    monitoreo: 'Monitoreo', tienda: 'Tienda', login: 'Entrar',
    misCotizaciones: 'Seguimiento de cotizaciones', ordenes: 'Órdenes y soporte',
    soporte: 'PQR y soporte', instalador: aud === 'technician' ? 'Instalar' : 'Quiero ser técnico',
    proveedor: 'Vender equipos', backoffice: 'Administración',
  };
  // NAV_DEF vive a nivel de módulo (arriba): el inicializador de `view` lo necesita.
  const cuotaEstudio = { quotes, ordenes };
  const veEstudio = veEstudioFotovoltaico(claims, cuotaEstudio);
  const veMon = veMonitoreo(claims, cuotaEstudio);
  const NAV_VISIBLE = NAV_DEF.filter(i => {
    if (i.id === 'estudio' && !veEstudio) return false;
    if (i.id === 'monitoreo' && !veMon) return false;
    if (i.id === 'servicios' && !veCatalogoServicios(claims)) return false;
    if (!i.aud || (!aud && HERRAMIENTAS_PUBLICAS.includes(i.id))) return navAllowed(i.id, aud, signed);
    return aud && i.aud.includes(aud) && navAllowed(i.id, aud, signed);
  });
  // Agrupar por sección preservando el orden de NAV_DEF
  const NAV_GROUPS = [];
  for (const it of NAV_VISIBLE) {
    let g = NAV_GROUPS.find(x => x.label === (it.section || null));
    if (!g) { g = { label: it.section || null, items: [] }; NAV_GROUPS.push(g); }
    g.items.push(it.id);
  }
  // No se concatenan SKUs de FluxAI al menú (SCADA / reactiva / ISO).
  // Solar Hub pide el dato (equipos+ficha) y pinta su vista. Ver docs/consumo-suite.md.
  // Móvil: máximo 5 ítems públicos. El riel de escritorio sigue con el menú completo.
  const MOBILE_PREF = ['home', 'estudio', 'quoter', 'tienda', 'misCotizaciones', 'ordenes'];
  const NAV_FLAT = MOBILE_PREF.filter((id) => NAV_VISIBLE.some((i) => i.id === id)).slice(0, 5);

  // Guard de ruta. 🔴 YA NO REBOTA EN SILENCIO.
  //
  // Antes estas cuatro lineas hacian `setView('home')` y ya: se hacia clic en un item del menu
  // y aparecia el Inicio, sin una palabra. El dueno lo llamo «saltos en los menus, nunca hay
  // continuidad» (8-sep) y tenia razon — el sistema decidia algo sobre esa persona y no se lo
  // contaba. Es el mismo defecto que este repo persigue en las pantallas: un vacio (o un salto)
  // se lee como un hecho, y «no te corresponde» y «no iniciaste sesion» son cosas distintas.
  //
  // Excepcion que se conserva: `backoffice` con sesion NULL mantiene `?view=backoffice&tab=` —
  // si se manda a home se pierde el deep-link (medido 2026-08-20).
  useEffect(() => {
    const def = NAV_DEF.find(i => i.id === view);
    const publica = HERRAMIENTAS_PUBLICAS.includes(view);
    const deepLinkAdmin = view === 'backoffice' && aud == null;
    const rebotar = (motivo, comoSigue) => {
      setAvisoRuta({ vista: NAV_LABELS[view] || view, motivo, comoSigue });
      setView('home');
    };
    if (def && def.aud && !(aud && def.aud.includes(aud)) && !(aud == null && publica) && !deepLinkAdmin) {
      rebotar(
        aud ? 'es de otro perfil' : 'pide que inicies sesion',
        aud ? 'Si te corresponde, pidele al administrador que ajuste tu perfil.' : 'Entra con tu cuenta y vuelve a abrirla.',
      );
    } else if (view === 'estudio' && !veEstudio) {
      rebotar('es un servicio pago', 'Se habilita al contratarlo o con una cotizacion en curso.');
    } else if (view === 'monitoreo' && !veMon) {
      rebotar('se habilita con un equipo instalado', 'Aparece cuando tengas telemetria activa.');
    } else if (view === 'servicios' && !veCatalogoServicios(claims)) {
      rebotar('es para clientes de la suite', 'Entra con tu cuenta para verlo.');
    } else if (avisoRuta) {
      // Al llegar a una vista que SI corresponde, el aviso viejo se va: nada confunde mas que un
      // cartel explicando un rebote que ya no esta pasando.
      setAvisoRuta(null);
    }
  }, [view, aud, veEstudio, veMon, claims]);
  useEffect(() => {
    const t = NAV_LABELS[view] || 'Solar Hub';
    document.title = view === 'home' ? 'Solar Hub — Estudio fotovoltaico' : `${t} · Solar Hub`;
    return () => { document.title = 'Solar Hub — Estudio fotovoltaico'; };
  }, [view]);

  return (
    <div style={{ minHeight: '100vh', background: C.dark, color: C.text, display: 'flex' }}>
      {/* ── SIDEBAR LATERAL (estilo FluxAI) — desktop ── */}
      <aside className="al-sidebar al-rail" style={{
        flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start',
        height: '100vh', display: 'flex', flexDirection: 'column', overflowX: 'hidden',
        background: `linear-gradient(180deg, ${C.nav} 0%, ${C.nav2} 100%)`,
        borderRight: '1px solid rgba(1,112,139,0.3)', zIndex: 99,
      }}>
        {/* Logo — MISMA altura que el header superior (40px) para que la línea inferior sea continua */}
        <div onClick={() => setView('home')} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none', height: 32, padding: '0 14px', boxSizing: 'border-box', borderBottom: `1px solid ${C.border}` }}>
          <svg viewBox="0 0 40 40" width="26" height="26" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <defs>
              <radialGradient id="sunGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#F9B000"/>
                <stop offset="100%" stopColor="#EB5D0B"/>
              </radialGradient>
            </defs>
            {[0,60,120,180,240,300].map((deg,i) => {
              const rad = (deg - 90) * Math.PI / 180;
              const x1 = 20 + 10 * Math.cos(rad), y1 = 20 + 10 * Math.sin(rad);
              const x2 = 20 + 18 * Math.cos(rad), y2 = 20 + 18 * Math.sin(rad);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={i%2===0?"#EB5D0B":"#F9B000"} strokeWidth="1.8" strokeLinecap="round"/>;
            })}
            {[0,60,120,180,240,300].map((deg,i) => {
              const rad = (deg - 90) * Math.PI / 180;
              const x = 20 + 18.5 * Math.cos(rad), y = 20 + 18.5 * Math.sin(rad);
              return <circle key={i} cx={x} cy={y} r="2.2" fill={i%2===0?"#EB5D0B":"#F9B000"}/>;
            })}
            <circle cx="20" cy="20" r="9" fill="url(#sunGrad)"/>
            <circle cx="20" cy="20" r="4.5" fill="#002430"/>
            <circle cx="20" cy="20" r="2.2" fill="#F9B000"/>
          </svg>
          <div className="al-rail-label" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <span style={{ fontWeight: 900, fontSize: 18, letterSpacing: '-0.5px', color: C.heading, lineHeight: 1 }}>Solar</span>
              <span style={{ fontWeight: 900, fontSize: 18, letterSpacing: '-0.5px', color: C.yellow, lineHeight: 1 }}>Hub</span>
            </div>
          </div>
        </div>

        {/* Nav vertical agrupada por secciones */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV_GROUPS.map((g, gi) => (
            <div key={gi} style={{ marginTop: gi === 0 ? 0 : 10 }}>
              {g.label && (
                <div className="al-rail-label" style={{ fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 12px 4px', opacity: 0.7 }}>
                  {g.label}
                </div>
              )}
              {g.items.map(item => {
                const esSvc = typeof item === 'object' && item.svc;
                const on = esSvc ? (item.ruta ? view === item.ruta : (view === 'fluxaiEmbed' && fluxSku === item.sku)) : view === item;
                const onClick = esSvc ? () => (item.ruta ? setView(item.ruta) : openFlux(item.sku)) : () => setView(item);
                if (esSvc) {
                  return (
                    <button type="button" key={'svc_' + item.sku} onClick={onClick} title={`${item.label} · potenciado por FluxAI`} className={`al-nav-item${on ? ' on' : ''}`}>
                      <span style={{ flexShrink: 0, display: 'inline-flex' }}><ServiceIcon sku={item.sku} size={16} /></span>
                      <span className="al-rail-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                    </button>
                  );
                }
                return (
                  <button type="button" key={item} onClick={onClick} title={NAV_LABELS[item]} className={`al-nav-item${on ? ' on' : ''}`}>
                    <span style={{ flexShrink: 0, display: 'inline-flex' }}><NavIcon id={item} size={16} /></span>
                    <span className="al-rail-label">{NAV_LABELS[item]}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

      </aside>

      {/* ── MAIN (contenido) ── */}
      <div className="al-main sh-grid-bg" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Header superior (escritorio) — barra delgada estilo consola FluxAI: reloj, estado,
            correo, chip de rol, suite y controles en ICONOS (densidad de software, no de web). */}
        <header className="al-topbar" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '0 16px', height: 32, position: 'sticky', top: 0, zIndex: 96,
          background: C.glass, borderBottom: `1px solid ${C.border}`,
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        }}>
          {view !== 'home' && (
            <button type="button" className="sh-btn sh-btn-ghost sh-btn-sm" onClick={() => setView('home')} title="Volver" aria-label="Volver">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              <span className="al-tagline-desktop">Volver</span>
            </button>
          )}
          <button type="button" className="sh-btn sh-btn-icon" onClick={() => setView('home')} title="Inicio" aria-label="Inicio"
            style={{ color: view === 'home' ? C.teal : undefined, borderColor: 'transparent' }}>
            <NavIcon id="home" size={15} />
          </button>

          <div style={{ flex: 1 }} />

          {claims && <HeaderClock />}
          {claims && <HeaderStatus quotes={quotes} ordenes={ordenes} aud={aud} />}
          {claims?.email && (
            <span className="al-tagline-desktop" title={claims.email} style={{ fontSize: 11, color: C.muted, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{claims.email}</span>
          )}
          {claims && (
            <span title="Sesión de la suite" style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: `${C.teal}18`, border: `1px solid ${C.teal}44`, color: C.teal, whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {profileLabel(claims)}
            </span>
          )}
          <SuiteLauncher />
          <button type="button" className="sh-btn sh-btn-icon" onClick={() => setTheme(THEME === 'light' ? 'dark' : 'light')}
            title={THEME === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'} aria-label="Cambiar tema">
            {THEME === 'light'
              ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"/></svg>
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"/></svg>}
          </button>
          {!claims && (
            <button onClick={() => setView('login')} title="Entrar" aria-label="Entrar"
              style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${C.yellow}66`, background: `${C.yellow}18`, color: C.yellow, cursor: 'pointer', fontSize: 11, fontWeight: 800 }}>
              Entrar
            </button>
          )}
          {claims && (
            <button type="button" className="sh-btn sh-btn-icon" onClick={logout} title="Cerrar sesión" aria-label="Cerrar sesión" style={{ color: C.red, borderColor: `${C.red}33` }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></svg>
            </button>
          )}
        </header>
        {/* Header móvil: logo + tema (la navegación va en el bottom-nav) */}
        <header className="al-mobile-top" style={{
          display: 'none', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 14px', height: 52, position: 'sticky', top: 0, zIndex: 97,
          background: `linear-gradient(180deg, ${C.nav} 0%, ${C.nav2} 100%)`,
          borderBottom: '1px solid rgba(1,112,139,0.3)',
        }}>
          <div onClick={() => setView('home')} style={{ display: 'flex', alignItems: 'baseline', gap: 1, cursor: 'pointer' }}>
            <span style={{ fontWeight: 900, fontSize: 17, color: C.heading }}>Solar</span>
            <span style={{ fontWeight: 900, fontSize: 17, color: C.yellow }}>Hub</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {claims && <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileLabel(claims)}</span>}
            <SuiteLauncher />
            <button type="button" className="sh-btn sh-btn-icon" onClick={() => setTheme(THEME === 'light' ? 'dark' : 'light')} aria-label="Cambiar tema">
              {THEME === 'light'
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4"/></svg>}
            </button>
            {claims && <button type="button" className="sh-btn sh-btn-icon" onClick={logout} aria-label="Cerrar sesión" style={{ color: C.red, borderColor: `${C.red}33` }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></svg>
            </button>}
          </div>
        </header>

      {/* ── BOTTOM NAV MOBILE ── */}
      <nav className="al-bottomnav" style={{
        display: 'none',
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
        background: C.glass,
        borderTop: '1px solid rgba(1,112,139,0.25)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.5)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'stretch', height: 58 }}>
          {NAV_FLAT.map(id => (
            <button key={id} onClick={() => setView(id)} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 3,
              border: 'none', cursor: 'pointer', background: 'transparent',
              color: view === id ? C.yellow : C.muted,
              transition: 'color .15s',
              position: 'relative',
            }}>
              {view === id && (
                <div style={{
                  position: 'absolute', top: 0, left: '25%', right: '25%',
                  height: 2, background: C.yellow,
                  borderRadius: '0 0 2px 2px',
                }} />
              )}
              <NavIcon id={id} size={20} />
              <span style={{ fontSize: 9, fontWeight: view===id ? 700 : 400, letterSpacing: '0.3px' }}>{NAV_LABELS[id]}</span>
            </button>
          ))}
        </div>
      </nav>

      {view === 'login' && <Login onDone={(dest) => setView(dest || 'home')} />}
      {/* 🔴 EL AVISO DEL REBOTE. Sin esto, el clic en un item del menu terminaba en el Inicio
          sin una palabra — «saltos en los menus, nunca hay continuidad» (dueno, 8-sep). Dice QUE
          se intento abrir, POR QUE no se pudo y QUE sigue. Cierra solo al navegar a algo valido. */}
      {view === 'home' && avisoRuta && (
        <div role="status" style={{
          margin: '12px 16px 0', padding: '10px 14px', borderRadius: 10,
          border: `1px solid ${C.amber}55`, background: `${C.amber}14`,
          display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, lineHeight: 1.45,
        }}>
          <span aria-hidden style={{ fontSize: 15, lineHeight: '1.2' }}>💡</span>
          <div style={{ flex: 1, color: C.text }}>
            <strong>{avisoRuta.vista}</strong>{` ${avisoRuta.motivo}, por eso volviste al inicio.`}
            <div style={{ color: C.muted, marginTop: 2 }}>{avisoRuta.comoSigue}</div>
          </div>
          <button type="button" onClick={() => setAvisoRuta(null)} aria-label="Cerrar aviso"
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
      )}
      {view === 'home' && <Home go={setView} openFlux={openFlux} quotes={quotes} ordenes={ordenes} sincro={sincro} />}
      {view === 'servicios' && veCatalogoServicios(claims) && <Servicios go={setView} openFlux={openFlux} quotes={quotes} ordenes={ordenes} />}
      {view === 'tienda' && <Tienda go={setView} />}
      {view === 'monitoreo' && <Monitoreo quotes={quotes} />}
      {view === 'fluxaiEmbed' && <FluxaiEmbed sku={fluxSku} go={setView} />}
      {/* 🔗 EL ESTUDIO DE ALTO NIVEL LO SIRVE FLUXAI Y ACÁ SE LEE (dueño 2026-08-09: «se queda
          en FluxAI… debe verse y migrarse a Solar Hub, para que el usuario pueda verlo en Solar
          Hub»). Se lee por su handle `fx:` y trae la memoria técnica RETIE renderizada allá.

          NO se embebe la app de FluxAI por iframe: eso exige el SSO firmado, que hoy solo
          funciona para los correos de la allowlist y deja al resto mirando un login ajeno
          (ver `FluxaiEmbed`). Leer el contrato no depende de una sesión ajena. */}
      {/* EL ESTUDIO tiene su PROPIA superficie (`Estudio`), separada del cotizador gratuito: es el
          producto pago, el documento de construcción para el técnico. Orquesta las dos piezas que
          ya existen — el lector del estudio canónico (`EstudioFlux`) y el motor de detalle
          (`Quoter alto`) — sin re-cablear sus props. El cotizador gratuito monta el Quoter directo. */}
      {view === 'estudio' && (
        <Estudio
          fxHandle={fxHandle}
          quotes={quotes}
          lector={<EstudioFlux handleInicial={fxHandle} />}
          constructor={(semilla) => (
            <Quoter
              panels={fichasPaneles} inverters={fichasInversores}
              batteries={fichasBaterias} pricing={pricing}
              operators={operators}
              addQuote={addQ}
              loadsCatalog={loadsCatalog}
              go={setView}
              alto
              semilla={semilla}
            />
          )}
        />
      )}
      {view === 'quoter' && (
        <Quoter
          panels={fichasPaneles} inverters={fichasInversores}
          batteries={fichasBaterias} pricing={pricing}
          operators={operators}
          addQuote={addQ}
          loadsCatalog={loadsCatalog}
          go={setView}
          alto={false}
        />
      )}
      {view === 'misCotizaciones' && <MisCotizaciones sincro={sincro.quotes} quotes={quotes} claims={claims} go={setView} updQuote={updQ} onAceptar={aceptarCotizacion} />}
      {view === 'ordenes' && <Ordenes sincro={sincro.ordenes} ordenes={ordenes} addOrden={addOrden} updOrden={updOrden} quotes={quotes} installers={installers} claims={claims} go={setView} />}
      {view === 'soporte' && <Ordenes sincro={sincro.ordenes} ordenes={ordenes} addOrden={addOrden} updOrden={updOrden} quotes={quotes} installers={installers} claims={claims} go={setView} soloSoporte />}
      {view === 'instalador' && <InstallerReg addInstaller={addInst} />}
      {/* Render inline en el shell para que el tab "Proveedores" de la navbar
          muestre algo. La versión full-page (con navbar/footer ocultos) se sigue
          activando cuando hay token de sesión: /?view=proveedor&token=UUID. */}
      {view === 'proveedor' && <SupplierPortal />}
      {/* Backoffice SOLO por rol (audiencia admin). Sin login propio: el acceso lo da el
          login diferencial de la suite (rol admin). Otros roles ni lo ven ni entran. */}
      {view === 'backoffice' && hydrated && aud === 'admin' && (
        <BackOffice sincro={sincro}
          tab={boTab} setTab={setBoTab}
          panels={panels} uP={uP}
          inverters={inverters} uI={uI}
          batteries={batteries} uB={uB}
          pricing={pricing} uPr={uPr}
          operators={operators} uOp={uOp}
          quotes={quotes} installers={installers}
          ordenes={ordenes} updOrden={updOrden}
          suppliers={suppliers} uSupp={uSupp}
          loadsCatalog={loadsCatalog} loadsSource={loadsSource}
          setLoadsCatalog={setLoadsCatalog} setLoadsSource={setLoadsSource}
        />
      )}
      {view === 'backoffice' && hydrated && aud !== 'admin' && (
        <div style={{ maxWidth: 480, margin: '48px auto', padding: '24px 20px', border: `1px solid ${C.border}`, borderRadius: 10, background: C.card }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.heading, marginBottom: 8 }}>Administración</div>
          <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5, margin: '0 0 10px' }}>
            Esta vista es del operador de la suite (precios, servicios, catálogo). El enlace se
            conserva: {boTab && boTab !== 'dashboard' ? `tab=${boTab}` : 'dashboard'}.
          </p>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, margin: 0 }}>
            Entrá con una cuenta admin (botón Entrar arriba). No se manda a Inicio para no perder el deep-link.
          </p>
        </div>
      )}

        {/* Pie de página de consola — delgado, sticky abajo, estado en vivo + marca (estilo FluxAI). */}
        <footer className="al-foot-slim" style={{ marginTop: 'auto', position: 'sticky', bottom: 0, zIndex: 20, borderTop: `1px solid ${C.border}`, background: C.glass, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: '0 16px', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: C.muted }}>
            <span className="sh-pulse-dot" style={{ width: 6, height: 6, borderRadius: 999, background: C.green, display: 'inline-block' }} />
            <strong style={{ color: C.heading, fontWeight: 700 }}>Solar<span style={{ color: C.yellow }}>Hub</span></strong>
            <span>· Marketplace de energía solar</span>
          </span>
          <span className="al-tagline-desktop" style={{ fontSize: 11, color: C.muted, display: 'flex', alignItems: 'center', gap: 10 }}>
            <a href="https://alebas.co/?linea=solarhub#radicar" style={{ color: C.teal, textDecoration: 'none', fontWeight: 700 }}>PQRS</a>
            <a href="/privacidad" style={{ color: C.teal, textDecoration: 'none', fontWeight: 700 }}>Privacidad</a>
            <a href="/terminos" style={{ color: C.teal, textDecoration: 'none', fontWeight: 700 }}>Términos</a>
            ALEBAS Ingeniería S.A.S. · CREG 174/2021 · RETIE · © {new Date().getFullYear()}
          </span>
        </footer>
      </div>
      {/* Alex — el asistente ÚNICO de la suite (cerebro FluxAI), flotante en toda la consola. */}
      <AlexLauncher />
    </div>
  );
}

// Frente comercial sin muro: cotizador, estudio y tienda se ven sin sesión.
// Login queda como vista (checkout / admin). Rutas con token propio siguen fuera.
function AuthGate() {
  return <AppShell />;
}

export default function App() {
  // Puente SSO entrante: una sola credencial en toda la suite (FluxAI/app → Solar Hub).
  ingestSsoToken();
  ingestTemaSuite();
  const urlParams = new URLSearchParams(window.location.search);
  const urlView = urlParams.get('view');
  const token = urlParams.get('token');
  // Pública (Ley 1581): no pasa por el shell. vercel.json reescribe /privacidad → index.html.
  const path = (typeof window !== 'undefined' ? window.location.pathname : '').replace(/\/+$/, '') || '/';
  if (path === '/privacidad' || urlView === 'privacidad') {
    return <Privacidad />;
  }
  // Pública, igual que la de privacidad: Verifik exige una URL de términos por proyecto.
  if (path === '/terminos' || urlView === 'terminos') {
    return <Terminos />;
  }
  if (urlView === 'proveedor') {
    return <SupplierPortal token={token} />;
  }
  if (urlView === 'tecnico') {
    return <TechnicianPortal token={token} />;
  }
  if (urlView === 'seguimiento') {
    // El enlace que arma `buildTrackingUrl` es `?view=seguimiento&id=<n>&t=<token>`: el token
    // viene en `t`, no en `token`. Leer solo `token` dejaba la página con "Token no
    // proporcionado" en TODOS los enlaces que se le enviaron al cliente.
    return <QuoteTracking token={token || urlParams.get('t')} quoteId={urlParams.get('id')} />;
  }
  return <AuthGate />;
}
